import { z } from 'zod';

import {
  correlationIdSchema,
  isoDateTimeSchema,
  nonNegativeIntSchema,
  positiveIntSchema,
  sha256HexSchema,
  slugSchema,
  uuidSchema,
} from '../common/primitives';
import { resourceRequestSchema } from '../fleet/capacity';

import { blueprintReferenceSchema } from './blueprint';
import {
  operationErrorSchema,
  operationStatusSchema,
  operationStepSchema,
  operationTypeSchema,
} from './operation';
import { siteDesiredStateSchema, siteObservedStateSchema } from './site-state';

/**
 * Die Außensicht auf eine Website und ihre Operationen.
 *
 * Diese Schemas standen bis M7 als TypeScript-Schnittstellen im Master
 * (`application/site-view.mapper.ts`). Mit dem Adminportal bekommen sie eine
 * zweite Gegenstelle, und damit gilt für sie dasselbe wie für alles andere
 * zwischen zwei Prozessen: Der Vertrag ist Code. Eine im Master umbenannte
 * Spalte, die in der Oberfläche als `undefined` ankäme, scheitert jetzt im
 * Typecheck.
 *
 * Was hier **nicht** steht, ist ebenso Absicht: keine Spezifikation, keine
 * Zugangsdaten, kein Node-Token. Die Abbildung ist eine ausdrückliche Auswahl
 * und keine Serialisierung der Entität — sonst ginge das nächste ergänzte Feld
 * automatisch mit hinaus.
 */

export const siteViewSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  /** Der interne Host-Header — Diagnose- und Rückfallweg, immer erreichbar. */
  hostname: z.string(),
  /**
   * Alle Host-Header, die der Worker für diese Site annimmt: der interne Name
   * und jede verifizierte Kundendomain.
   *
   * Gelesen aus der **Spezifikation**, nicht aus der Domänentabelle. Das ist
   * der Unterschied zwischen „eingetragen" und „läuft dort tatsächlich" — und
   * genau die Frage, die man im Störfall stellt.
   */
  hostnames: z.array(z.string()),
  nodeId: uuidSchema,
  blueprint: blueprintReferenceSchema,
  /** Was laufen soll. */
  desiredState: siteDesiredStateSchema,
  /** Was der Node gemeldet hat. */
  observedState: siteObservedStateSchema,
  observedAt: isoDateTimeSchema.nullable(),
  /** `true`, wenn auf dem Node die Spezifikation läuft, die laufen soll. */
  inSync: z.boolean(),
  reservedResources: resourceRequestSchema,
  /**
   * Die XFS-Projektnummer dieser Site. `null` auf einer Site aus der Zeit vor
   * M6b, bis der Auffrischungslauf sie nachträgt.
   *
   * Sie steht hier, weil das Runbook sie braucht: `xfs_quota report -p` auf
   * dem Worker liefert Zahlen je Projekt, und diese Zeile ist der Weg von
   * einer solchen Zahl zurück zu einer Website.
   */
  quotaProjectId: positiveIntSchema.nullable(),
  consecutiveFailures: nonNegativeIntSchema,
  nextAttemptAt: isoDateTimeSchema.nullable(),
  lastError: z.string().nullable(),
  reconcilePaused: z.boolean(),

  /**
   * Was der Reconciler zuletzt entschieden hat, und warum.
   *
   * `none` heißt: Er hat hingesehen und sich bewusst gegen eine Handlung
   * entschieden — die Begründung sagt, weshalb. Das ist der Unterschied
   * zwischen „hängt" und „wartet mit gutem Grund", und ohne diese Felder war er
   * von außen nicht zu erkennen (siehe Migration 1754990300000).
   *
   * `null` bei einer Site, die der Reconciler seit dem Rollout noch nicht
   * angesehen hat. Nicht als leerer Text vorbelegt: „noch nichts entschieden"
   * und „nichts zu tun" sind zwei verschiedene Aussagen.
   */
  lastReconcileAction: z.string().nullable(),
  lastReconcileReason: z.string().nullable(),
  /** Seit wann diese Begründung gilt — nicht, wann zuletzt geprüft wurde. */
  lastReconcileAt: isoDateTimeSchema.nullable(),

  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export type SiteView = z.infer<typeof siteViewSchema>;

export const operationViewSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  nodeId: uuidSchema,
  type: operationTypeSchema,
  status: operationStatusSchema,
  attempts: nonNegativeIntSchema,
  specHash: sha256HexSchema.nullable(),
  correlationId: correlationIdSchema,
  dispatchedAt: isoDateTimeSchema.nullable(),
  finishedAt: isoDateTimeSchema.nullable(),
  error: operationErrorSchema.nullable(),
  steps: z.array(operationStepSchema),
  createdAt: isoDateTimeSchema,
});

export type OperationView = z.infer<typeof operationViewSchema>;

/**
 * Eine Operation in der flottenweiten Liste.
 *
 * Sie trägt den Namen ihrer Site mit. Ohne ihn zeigte die Liste vierzig Zeilen
 * mit UUIDs, und die Oberfläche müsste für jede eine zweite Abfrage stellen —
 * genau das N+1, das eine Liste unbrauchbar macht. Die Kopie ist unkritisch:
 * Sie wird bei jedem Abruf frisch gelesen, nicht gespeichert.
 */
export const operationListItemSchema = operationViewSchema.extend({
  siteSlug: slugSchema,
  siteHostname: z.string(),
});

export type OperationListItem = z.infer<typeof operationListItemSchema>;

export const operationPageSchema = z.object({
  operations: z.array(operationListItemSchema),
  /** Zeiger auf die nächste Seite, `null` am Ende. Begründung wie beim Prüfprotokoll. */
  nextCursor: z.string().nullable(),
});

export type OperationPage = z.infer<typeof operationPageSchema>;
